import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

class GoogleAuthService {
  constructor() {
    this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || window.googleClientId;
    this.initialized = false;
    console.log('GoogleAuthService initialized with client ID:', this.clientId);
  }

  async initGoogleSignIn() {
    if (this.initialized) {
      console.log('Google Sign-In already initialized');
      return;
    }

    try {
      if (!this.clientId) {
        throw new Error('Google Client ID is not configured');
      }

      console.log('Initializing Google Sign-In with client ID:', this.clientId);

      // Load the Google Sign-In script
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          console.log('Google Sign-In script loaded successfully');
          resolve();
        };
        script.onerror = (error) => {
          console.error('Failed to load Google Sign-In script:', error);
          reject(new Error('Failed to load Google Sign-In script. Please check your internet connection.'));
        };
        document.head.appendChild(script);
      });

      // Wait for Google to be available
      await new Promise((resolve) => {
        const checkGoogle = () => {
          if (window.google && window.google.accounts) {
            resolve();
          } else {
            setTimeout(checkGoogle, 100);
          }
        };
        checkGoogle();
      });

      // Initialize Google Sign-In
      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: this.handleGoogleCallback.bind(this),
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signup',
        ux_mode: 'popup'
      });

      this.initialized = true;
      console.log('Google Sign-In initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Google Sign-In:', error);
      throw new Error('Failed to initialize Google Sign-In. Please try again later.');
    }
  }

  async handleGoogleSignIn(isRegistration = false) {
    try {
      await this.initGoogleSignIn();
      
      // Get the existing container
      const buttonContainer = document.getElementById('google-signin-button');
      if (!buttonContainer) {
        console.error('Google Sign-In button container not found');
        throw new Error('Google Sign-In button container not found. Please try again later.');
      }
      
      // Clear any existing content in the container
      buttonContainer.innerHTML = '';
      
      // Render the Google Sign-In button in the existing container
      window.google.accounts.id.renderButton(
        buttonContainer,
        { 
          type: 'standard',
          theme: 'outline',
          // Available size options: 'small', 'medium', 'large'
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width: buttonContainer.offsetWidth || 400,
          height: 50,
          locale: "en"
        }
      );

      // Prompt the user to select an account
      window.google.accounts.id.prompt();
      
      // Return a promise that will be resolved when the callback is triggered
      return new Promise((resolve, reject) => {
        this.resolveCallback = resolve;
        this.rejectCallback = reject;
        this.isRegistration = isRegistration;
      });
    } catch (error) {
      console.error('Error during Google Sign-In:', error);
      throw error;
    }
  }

  /**
   * Set authorization header for future API requests
   * @param {string} token - JWT token
   */
  setAuthHeader(token) {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }

  async handleGoogleCallback(googleResponse) {
    if (!googleResponse.credential) {
      const error = new Error('No credential received from Google');
      if (this.rejectCallback) {
        this.rejectCallback(error);
      }
      return;
    }

    const endpoint = this.isRegistration ? '/auth/google/register' : '/auth/google/login';
    
    try {
      const response = await axios.post(`${API_URL}${endpoint}`, {
        credential: googleResponse.credential
      });

      // Store JWT token in localStorage if available
      if (response.data && response.data.token) {
        localStorage.setItem('token', response.data.token);
        
        // Set authorization header
        this.setAuthHeader(response.data.token);
        
        // Store refresh token if available
        if (response.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.refreshToken);
        }
      }

      // Check if the response includes user data and email verification status
      if (response.data && response.data.user) {
        if (!response.data.user.emailVerified) {
          const warning = new Error('Email not verified');
          warning.code = 'EMAIL_NOT_VERIFIED';
          warning.message = 'Please verify your email address before accessing the dashboard.';
          warning.user = response.data.user;
          if (this.rejectCallback) {
            this.rejectCallback(warning);
          }
          return;
        }
      }

      if (this.resolveCallback) {
        this.resolveCallback(response.data);
      }
      return response.data;
    } catch (axiosError) {
      console.error('Error handling Google callback:', axiosError);
      
      let error;
      
      if (!axiosError.response) {
        if (axiosError.request) {
          error = new Error('Network error. Please check your connection and try again.');
        } else {
          error = new Error('An unexpected error occurred. Please try again.');
        }
      } else {
        const status = axiosError.response.status;
        const data = axiosError.response.data;

        switch (status) {
          case 404:
            // Enhanced account not found error with more complete information
            error = new Error('Account not found');
            error.code = 'ACCOUNT_NOT_FOUND';
            error.redirectToRegister = true;
            
            // Extract as much Google user information as possible to pass to registration
            if (googleResponse && googleResponse.credential) {
              try {
                // Try to decode the JWT to get user info directly
                // This is a fallback in case the server didn't return the email
                const base64Url = googleResponse.credential.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                  return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));

                const payload = JSON.parse(jsonPayload);
                
                // Set user details from the decoded token
                error.email = data?.email || payload.email;
                error.name = data?.name || payload.name || (payload.given_name && payload.family_name ? 
                            `${payload.given_name} ${payload.family_name}` : payload.name);
                error.picture = data?.picture || payload.picture;
              } catch (decodeErr) {
                console.log('Error decoding Google credential:', decodeErr);
                // Still set the email if it was in the response
                error.email = data?.email;
              }
            } else {
              // Fallback to just the email from the response if available
              error.email = data?.email;
            }
            
            error.message = 'No account found with this Google account. Please register first.';
            break;

          case 401:
            error = new Error('Invalid credentials');
            break;

          case 409:
            if (this.isRegistration) {
              error = new Error('Account already exists');
              error.code = 'ACCOUNT_EXISTS';
              error.redirectToLogin = true;
              error.email = data?.email;
              error.message = 'This email is already registered. Please login instead.';
            } else {
              error = new Error('Account already exists');
            }
            break;

          case 400:
            error = new Error(data?.message || 'Invalid request');
            error.code = 'BAD_REQUEST';
            error.message = data?.message || 'Invalid request. Please try again.';
            break;

          default:
            error = new Error(data?.message || 'Authentication failed');
        }
      }

      // IMPORTANT: Make sure we reject with the error before cleanup
      if (this.rejectCallback) {
        console.log('Rejecting with error:', error);
        
        // Create a slight delay to ensure the rejection happens
        // before any UI state gets reset
        setTimeout(() => {
          if (this.rejectCallback) {
            this.rejectCallback(error);
            
            // Only clean up AFTER rejecting 
            // This prevents the promise from being orphaned
            this.cleanup();
          }
        }, 100);
      } else {
        console.error('No reject callback found for Google auth error');
        this.cleanup();
      }
      
      // Don't return anything here, let the rejection handle it
    }
  }

  cleanup() {
    const buttonContainer = document.getElementById('google-signin-button');
    if (buttonContainer) {
      // Clear the container instead of removing it
      buttonContainer.innerHTML = '';
      console.log('Google button container cleaned up');
    }
    
    // Reset callbacks
    this.resolveCallback = null;
    this.rejectCallback = null;
  }
}

const googleAuthService = new GoogleAuthService();
export default googleAuthService;